import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";

import { TrashcanIcon } from "~/core/icons";

import { useIntl } from "@ai360/core/intl";

import {
    AutoSearch,
    Bucket,
    BucketHeader,
    Checkbox,
    DateInput,
    NumericInput,
    SelectInput,
    TextArea,
    TextInput,
} from "~/core";

import { getTheUserGuid } from "~/login";
import { PersonAPI, EquipmentProfileAPI } from "@ai360/core";
import { getAgEventTransactionTypeNameToFormMap } from "./transaction-type-form-map";

import {
    actions as recsEventsActions,
    eventsSelectors,
    selectors as recsEventsSelectors,
} from "~/recs-events";

import {
    ACTIVE_YN,
    actions as picklistActions,
    picklistNames,
    selectors as picklistSelectors,
} from "~/core/picklist";

import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { getModuleState } from "../selectors";
import { getSetValuesForErrorCodeList } from "../../../../common/validation-utils";

import "../../../../common/rec-event-info/rec-event-info.css";

import "./event-general-form.css";
import { messages } from "./../i18n-messages";

const {
    getPickListCode,
    PICKLIST_CROPPING_SEASON,
    PICKLIST_CROPPING_SEASON_CYCLE,
    PICKLIST_DENSITY_RATING,
    PICKLIST_DIRECTION,
    PICKLIST_GENERAL_WEATHER,
    PICKLIST_SOIL_CONDITION,
} = picklistNames;

const errorCodeToMessageIdSetMap = new Map<number, any>([
    [80, messages.seasonPlaceholderText], // ErrorCode.AgEventCropSeasonRequired
    [81, messages.startDatePlaceholderText], // ErrorCode.AgEventStartDatetimeRequired
    [2083, null], // ErrorCode.AgEventFieldRequired
    [2330, [messages.eventNamePlaceholderText, messages.startDatePlaceholderText]], // ErrorCode.AgEventTypeDateNameDuplicated
    [2380, null], // ErrorCode.EventAreaListEmpty
    [2383, null], // ErrorCode.EventAreaPolygonNotFound
    [2647, null], // ErrorCode.FieldBoundaryGuidRequired
    [2814, null], // ErrorCodes.FieldBoundaryMissingForWholeFieldZone
]);

export const errorCodesApply = (errorCodeList: number[]): boolean => {
    return errorCodeList.some((errorCode) => errorCodeToMessageIdSetMap.has(errorCode));
};

const eventCreationType = "Imported";

export const EventGeneralForm = memo(function EventGeneralForm() {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();

    // --- Redux state ---
    const { eventSummary, isLoading } = useAppSelector(getModuleState);
    const { fieldGuidToEventDetails, saveEventDetailsErrorCodeList } = useAppSelector(
        eventsSelectors.getModuleState
    );
    const { batchFieldGuid } = useAppSelector(recsEventsSelectors.getZonesState);
    const userGuid = useAppSelector(getTheUserGuid);

    const fieldGuid =
        batchFieldGuid != null
            ? batchFieldGuid
            : eventSummary != null
            ? eventSummary.fieldGuid
            : null;

    const eventDetails = !isLoading ? fieldGuidToEventDetails.get(fieldGuid) : null;

    const picklistOptionsCroppingSeason = useAppSelector((state) =>
        picklistSelectors.getPicklistOptionsFromCode(
            state,
            getPickListCode(PICKLIST_CROPPING_SEASON)
        )
    );
    const picklistOptionsCroppingSeasonCycle = useAppSelector((state) =>
        picklistSelectors.getPicklistOptionsFromCode(
            state,
            getPickListCode(PICKLIST_CROPPING_SEASON_CYCLE)
        )
    );
    const picklistOptionsDirection = useAppSelector((state) =>
        picklistSelectors.getPicklistOptionsFromCode(state, getPickListCode(PICKLIST_DIRECTION))
    );
    const picklistOptionsGeneralWeather = useAppSelector((state) =>
        picklistSelectors.getPicklistOptionsFromCode(
            state,
            getPickListCode(PICKLIST_GENERAL_WEATHER)
        )
    );
    const picklistOptionsSoilCompaction = useAppSelector((state) =>
        picklistSelectors.getPicklistOptionsFromCode(
            state,
            getPickListCode(PICKLIST_DENSITY_RATING)
        )
    );
    const picklistOptionsSoilCondition = useAppSelector((state) =>
        picklistSelectors.getPicklistOptionsFromCode(
            state,
            getPickListCode(PICKLIST_SOIL_CONDITION)
        )
    );

    // --- Local state ---
    const [includeTime, setIncludeTime] = useState(false);
    const [haveSetDefaultValues, setHaveSetDefaultValues] = useState(false);

    const errorMessagePlaceholderSet = useMemo(
        () =>
            getSetValuesForErrorCodeList(saveEventDetailsErrorCodeList, errorCodeToMessageIdSetMap),
        [saveEventDetailsErrorCodeList]
    );

    const agEventTransactionTypeNameToFormMapRef = useRef(null);

    const getAgEventTransactionTypeNameToFormMapCached = useCallback(() => {
        if (agEventTransactionTypeNameToFormMapRef.current == null) {
            agEventTransactionTypeNameToFormMapRef.current =
                getAgEventTransactionTypeNameToFormMap();
        }
        return agEventTransactionTypeNameToFormMapRef.current;
    }, []);

    // --- Actions ---
    const updateEventDetails = useCallback(
        (newProps) => {
            dispatch(recsEventsActions.updateEventDetails(fieldGuid, newProps));
        },
        [dispatch, fieldGuid]
    );

    // --- Effects ---

    // Fetch picklists on mount
    useEffect(() => {
        const fetchPicklistNames = [
            PICKLIST_CROPPING_SEASON,
            PICKLIST_CROPPING_SEASON_CYCLE,
            PICKLIST_DENSITY_RATING,
            PICKLIST_DIRECTION,
            PICKLIST_GENERAL_WEATHER,
            PICKLIST_SOIL_CONDITION,
        ];

        const picklistFetchArgObj = fetchPicklistNames.reduce((accum, key) => {
            accum[key] = picklistNames.getPickListCode(key);
            return accum;
        }, {});

        dispatch(picklistActions.fetchPicklistData(picklistFetchArgObj));
    }, [dispatch]);

    // Set default cropping season once eventDetails and picklist options are available
    useEffect(() => {
        if (
            !haveSetDefaultValues &&
            eventDetails != null &&
            picklistOptionsCroppingSeason != null &&
            picklistOptionsCroppingSeason.length > 0
        ) {
            setHaveSetDefaultValues(true);
            if (!eventDetails.croppingSeasonGuid) {
                const curYearStr = String(new Date().getFullYear());
                const defaultOption = picklistOptionsCroppingSeason.find(
                    (option) => option.label === curYearStr
                );
                updateEventDetails({
                    croppingSeasonGuid: defaultOption && defaultOption.value,
                });
            }
        }
    }, [eventDetails, haveSetDefaultValues, picklistOptionsCroppingSeason, updateEventDetails]);

    // --- Event handlers ---

    const addPerson = useCallback(
        (person) => {
            const { personList } = eventDetails;
            if (
                person &&
                !personList.some((listPerson) => listPerson.personGuid === person.personGuid)
            ) {
                updateEventDetails({ personList: [...eventDetails.personList, person] });
            }
        },
        [eventDetails, updateEventDetails]
    );

    const addEquipment = useCallback(
        (equipment) => {
            const { equipmentList } = eventDetails;
            if (
                equipment &&
                !equipmentList.some(
                    (listEquipment) =>
                        listEquipment.equipmentProfileGuid === equipment.equipmentProfileGuid
                )
            ) {
                updateEventDetails({
                    equipmentList: [...eventDetails.equipmentList, equipment],
                });
            }
        },
        [eventDetails, updateEventDetails]
    );

    const removePerson = useCallback(
        (index) => {
            const newPersonList = [...eventDetails.personList];
            newPersonList.splice(index, 1);
            updateEventDetails({ personList: newPersonList });
        },
        [eventDetails, updateEventDetails]
    );

    const removeEquipment = useCallback(
        (index) => {
            const newEquipmentList = [...eventDetails.equipmentList];
            newEquipmentList.splice(index, 1);
            updateEventDetails({ equipmentList: newEquipmentList });
        },
        [eventDetails, updateEventDetails]
    );

    const getEventTypeMap = useCallback(
        (details) => {
            return new Map(
                details.agEventTypeList
                    .filter((agEventInfo) => {
                        const haveForm = getAgEventTransactionTypeNameToFormMapCached().has(
                            agEventInfo.agEventTransactionTypeName
                        );
                        if (!haveForm) {
                            console.warn(
                                "Ignoring unsupported `agEvent` type",
                                agEventInfo.typeName,
                                getAgEventTransactionTypeNameToFormMapCached()
                            );
                        }
                        return haveForm;
                    })
                    .map((agEventTypeInfo) => [
                        agEventTypeInfo.agEventTransactionTypeGuid,
                        agEventTypeInfo.agEventTransactionTypeName,
                    ])
            );
        },
        [getAgEventTransactionTypeNameToFormMapCached]
    );

    const getAutoSearchList = useCallback(
        (searchUserGuid, searchText) => {
            const agEventTypeGuidToNameMap = getEventTypeMap(eventDetails);
            return EquipmentProfileAPI.searchEquipmentWithName(searchUserGuid, {
                agEventTransactionTypeName: Array.from(agEventTypeGuidToNameMap.values()),
                searchText,
            });
        },
        [eventDetails, getEventTypeMap]
    );

    const getToolTip = useCallback((equipment) => {
        let tooltiptext = `${equipment.equipmentParentTypeName} - ${equipment.equipmentType} - ${equipment.equipmentName} - ${equipment.ownerOperatorName}`;
        if (equipment.controllerId.length > 0) {
            tooltiptext = tooltiptext + " - " + equipment.controllerId;
        }
        return tooltiptext;
    }, []);

    if (!eventDetails) {
        return null;
    }

    return (
        <div className="rec-event-info-form event-general-form">
            <Bucket
                showSymbol={false}
                isCollapsible={false}
                isExpanded
                testId="event-general-general-info-bucket"
            >
                <BucketHeader>{formatMessage(messages.generalInfoBucketTitle)}</BucketHeader>
                <div className="input-row half">
                    <TextInput
                        maxLength={50}
                        autoFocus={!eventDetails.eventName}
                        containerClassNames={[
                            {
                                "form-input-error": errorMessagePlaceholderSet.has(
                                    messages.eventNamePlaceholderText
                                ),
                            },
                        ]}
                        value={eventDetails.eventName}
                        onChange={(v) => updateEventDetails({ eventName: v })}
                        placeholderText={formatMessage(messages.eventNamePlaceholderText)}
                        testId="event-general-event-name-input"
                    />
                </div>
            </Bucket>
            <Bucket
                showSymbol={false}
                isCollapsible={false}
                isExpanded
                testId="event-general-timing-info-bucket"
            >
                <BucketHeader>{formatMessage(messages.timingInfoBucketTitle)}</BucketHeader>
                <div className="input-row">
                    <SelectInput
                        required
                        optionIsHiddenKey={ACTIVE_YN}
                        clearable={false}
                        containerClassNames={[
                            {
                                "select-form-input-error": errorMessagePlaceholderSet.has(
                                    messages.seasonPlaceholderText
                                ),
                            },
                        ]}
                        onChange={(v) => updateEventDetails({ croppingSeasonGuid: v })}
                        options={picklistOptionsCroppingSeason}
                        placeholderText={formatMessage(messages.seasonPlaceholderText)}
                        value={eventDetails.croppingSeasonGuid}
                        testId="event-general-season-select"
                    />
                    <SelectInput
                        optionIsHiddenKey={ACTIVE_YN}
                        onChange={(v) => updateEventDetails({ croppingSeasonCycleGuid: v })}
                        options={picklistOptionsCroppingSeasonCycle}
                        placeholderText={formatMessage(messages.cropCyclePlaceholderText)}
                        value={eventDetails.croppingSeasonCycleGuid}
                        testId="event-general-crop-cycle-select"
                    />
                </div>
                <div className="input-row">
                    <DateInput
                        required
                        clearDisabled
                        containerClassNames={[
                            {
                                "form-input-error": errorMessagePlaceholderSet.has(
                                    messages.startDatePlaceholderText
                                ),
                            },
                        ]}
                        onChange={(v) =>
                            updateEventDetails({
                                momentStartDate: moment(v),
                                momentStartTime: moment(v),
                            })
                        }
                        openDirection="left"
                        timeFormat={includeTime ? "h:mm a" : false}
                        placeholderText={formatMessage(messages.startDatePlaceholderText)}
                        value={eventDetails.momentStartDate}
                        testId="event-general-start-date-date"
                    />
                    <DateInput
                        required
                        clearDisabled
                        onChange={(v) =>
                            updateEventDetails({
                                momentEndDate: moment(v),
                                momentEndTime: moment(v),
                            })
                        }
                        openDirection="right"
                        timeFormat={includeTime ? "h:mm a" : false}
                        placeholderText={formatMessage(messages.endDatePlaceholderText)}
                        value={eventDetails.momentEndDate}
                        testId="event-general-end-date-date"
                    />
                </div>
                <Checkbox
                    label={formatMessage(messages.hasTimeLabel)}
                    value={includeTime}
                    onChange={(evt, v) => setIncludeTime(v)}
                    testId="event-general-include-time-checkbox"
                />
            </Bucket>
            <Bucket
                showSymbol={false}
                isCollapsible={false}
                isExpanded
                testId="event-general-weather-info-bucket"
            >
                <BucketHeader>{formatMessage(messages.weatherInfoBucketTitle)}</BucketHeader>
                <div className="input-row">
                    <SelectInput
                        optionIsHiddenKey={ACTIVE_YN}
                        onChange={(v) => updateEventDetails({ generalWeatherGuid: v })}
                        options={picklistOptionsGeneralWeather}
                        placeholderText={formatMessage(messages.weatherPlaceholderText)}
                        value={eventDetails.generalWeatherGuid}
                        testId="event-general-weather-select"
                    />
                    <NumericInput
                        precision={5}
                        scale={2}
                        allowNegatives={true}
                        containerClassNames={["temp-input"]}
                        value={eventDetails.airTemperature}
                        onChange={(v) => updateEventDetails({ airTemperature: v })}
                        placeholderText={formatMessage(messages.tempPlaceholderText)}
                        testId="event-general-air-temperature-input"
                    />
                    {/* Extra div so that the margin is included in the width calculation for flex-grow: 1 */}
                    <div>
                        <NumericInput
                            precision={5}
                            scale={2}
                            value={eventDetails.humidityPercentage}
                            onChange={(strVal, formattedVal, numVal) =>
                                updateEventDetails({ humidityPercentage: numVal })
                            }
                            placeholderText={formatMessage(messages.humidityPlaceholderText)}
                            testId="event-general-humidity-input"
                        />
                    </div>
                </div>
                <div className="input-row">
                    <SelectInput
                        optionIsHiddenKey={ACTIVE_YN}
                        onChange={(v) => updateEventDetails({ directionGuid: v })}
                        options={picklistOptionsDirection}
                        placeholderText={formatMessage(messages.windDirectionPlaceholderText)}
                        value={eventDetails.directionGuid}
                        testId="event-general-wind-direction-select"
                    />
                    <div>
                        <NumericInput
                            precision={5}
                            scale={2}
                            value={eventDetails.windSpeed}
                            onChange={(strVal, formattedVal, numVal) =>
                                updateEventDetails({ windSpeed: numVal })
                            }
                            placeholderText={formatMessage(messages.windSpeedPlaceholderText)}
                            testId="event-general-wind-speed-input"
                        />
                    </div>
                    <div>
                        <NumericInput
                            precision={6}
                            scale={2}
                            value={eventDetails.cropGrowingDays}
                            onChange={(strVal, formattedVal, numVal) =>
                                updateEventDetails({ cropGrowingDays: numVal })
                            }
                            placeholderText={formatMessage(messages.GDDPlaceholderText)}
                            testId="event-general-crop-growing-days-input"
                        />
                    </div>
                </div>
                <div className="input-row">
                    <NumericInput
                        precision={5}
                        scale={2}
                        value={eventDetails.precipitation}
                        onChange={(strVal, formattedVal, numVal) =>
                            updateEventDetails({ precipitation: numVal })
                        }
                        placeholderText={formatMessage(messages.precipitationPlaceholderText)}
                        testId="event-general-precipitation-input"
                    />
                    <NumericInput
                        precision={5}
                        scale={2}
                        value={eventDetails.cloudCoverPercentage}
                        onChange={(strVal, formattedVal, numVal) =>
                            updateEventDetails({ cloudCoverPercentage: numVal })
                        }
                        placeholderText={formatMessage(messages.cloudCoverPlaceholderText)}
                        testId="event-general-cloud-cover-input"
                    />
                </div>
            </Bucket>
            <Bucket
                showSymbol={false}
                isCollapsible={false}
                isExpanded
                testId="event-general-soil-info-bucket"
            >
                <BucketHeader>{formatMessage(messages.soilInfoBucketTitle)}</BucketHeader>
                <div className="input-row">
                    <SelectInput
                        optionIsHiddenKey={ACTIVE_YN}
                        onChange={(v) => updateEventDetails({ soilConditionGuid: v })}
                        options={picklistOptionsSoilCondition}
                        placeholderText={formatMessage(messages.soilCondPlaceholderText)}
                        value={eventDetails.soilConditionGuid}
                        testId="event-general-soil-condition-select"
                    />
                    <NumericInput
                        precision={5}
                        scale={2}
                        allowNegatives={true}
                        containerClassNames={["temp-input"]}
                        onChange={(strVal, formattedVal, numVal) =>
                            updateEventDetails({ soilTemperature: numVal })
                        }
                        placeholderText={formatMessage(messages.tempPlaceholderText)}
                        value={eventDetails.soilTemperature}
                        testId="event-general-soil-temperature-input"
                    />
                    <SelectInput
                        optionIsHiddenKey={ACTIVE_YN}
                        onChange={(v) => updateEventDetails({ densityRatingGuid: v })}
                        options={picklistOptionsSoilCompaction}
                        placeholderText={formatMessage(messages.soilCompactionPlaceholderText)}
                        value={eventDetails.densityRatingGuid}
                        testId="event-general-soil-compaction-select"
                    />
                </div>
            </Bucket>
            <Bucket
                showSymbol={false}
                isCollapsible={false}
                isExpanded
                testId="event-general-people-bucket"
            >
                <BucketHeader>{formatMessage(messages.peopleBucketTitle)}</BucketHeader>
                <div className="input-row">
                    <AutoSearch
                        clearOnSelection={true}
                        required={false}
                        itemList={[]}
                        initialFilterStr=""
                        onSelection={(p) => addPerson(p)}
                        getAutoSearchList={PersonAPI.searchPersonWithUser}
                        userGuid={userGuid}
                        placeholderText={formatMessage(messages.findPeoplePlaceholderText)}
                        keyProp="personGuid"
                        nameProp="personName"
                        secondaryPropList={["city", "stateAbbreviation", "jobTitleName"]}
                        excludedValues={eventDetails.personList.map((person) => person.personGuid)}
                    />
                </div>
                <div className="input-row">
                    <div className="person-list">
                        {eventDetails.personList.map((person, index) => (
                            <div key={`person-${index}`} className="person-item">
                                <div
                                    className="trashcan-icon-container"
                                    onClick={() => removePerson(index)}
                                >
                                    <TrashcanIcon />
                                </div>
                                <div className="person-info">{`${person.personName}`}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </Bucket>
            <Bucket
                showSymbol={false}
                isCollapsible={false}
                isExpanded
                testId="event-general-equipment-bucket"
            >
                <BucketHeader>{formatMessage(messages.equipmentBucketTitle)}</BucketHeader>
                {eventDetails.creationType !== eventCreationType ||
                eventDetails.canEditEquipment ? (
                    <div className="input-row">
                        <AutoSearch
                            clearOnSelection={true}
                            required={false}
                            itemList={[]}
                            initialFilterStr=""
                            onSelection={(p) => addEquipment(p)}
                            getAutoSearchList={getAutoSearchList}
                            userGuid={userGuid}
                            placeholderText={formatMessage(messages.findEquipmentPlaceholderText)}
                            keyProp="equipmentProfileGuid"
                            nameProp="equipmentName"
                            secondaryPropList={[
                                "equipmentParentTypeName",
                                "equipmentType",
                                "ownerOperatorName",
                            ]}
                            excludedValues={eventDetails.equipmentList.map(
                                (e) => e.equipmentProfileGuid
                            )}
                        />
                    </div>
                ) : null}
                <div className="input-row">
                    <div className="equipment-list">
                        {eventDetails.equipmentList.map((equipment, index) => (
                            <div
                                key={`equipment-${index}`}
                                className="equipment-item"
                                title={getToolTip(equipment)}
                            >
                                {eventDetails.creationType !== eventCreationType ||
                                eventDetails.canEditEquipment ? (
                                    <div
                                        className="equipment-trashcan-icon-container"
                                        onClick={() => removeEquipment(index)}
                                    >
                                        <TrashcanIcon />
                                    </div>
                                ) : (
                                    <div className="empty-container"></div>
                                )}
                                <div className="equipment-info">
                                    {equipment.equipmentParentTypeName + " - "}{" "}
                                    {equipment.equipmentName}{" "}
                                    {equipment.controllerId.length > 0
                                        ? " - " + equipment.controllerId
                                        : ""}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Bucket>
            <Bucket
                showSymbol={false}
                isCollapsible={false}
                isExpanded
                testId="event-general-notes-bucket"
            >
                <BucketHeader>{formatMessage(messages.generalNotesBucketTitle)}</BucketHeader>
                <TextArea
                    value={eventDetails.notes}
                    onChange={(v) => updateEventDetails({ notes: v })}
                    placeholderText={formatMessage(messages.notesPlaceholderText)}
                    maxLength={1000}
                    testId="event-general-notes-textarea"
                />
            </Bucket>
        </div>
    );
});
